/**
 * ReportHeader — the page head of a report (nuevo look Omnitok, iteración
 * 2, 2026-10-24): breadcrumb, title, description and the actions on the
 * right, replacing the navy gradient card the portal carried since v1.
 * The specification is the approved v2 report (Fase C7): 25px display
 * title in the body tone, 12px muted description, 11px breadcrumb, and
 * actions of 32px. The head sits on the page canvas; the host decides
 * any band around it.
 *
 * Shared by the portal page and the builder Preview so both render the
 * same head (rule 8: no mirror of the composition). Presentation only:
 *
 * Encabezado compacto H1c (Pablo, 2026-10-26): title and description on
 * the left and the actions on the right share ONE row from ``md`` up.
 * A long description used to take the whole row and push the actions
 * to a second line; now it wraps inside its column and stops at two
 * lines (the rest in the tooltip). Title 22px.
 * what the breadcrumb links to and what the actions do belong to the
 * host.
 */
import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";

export interface ReportBreadcrumbItem {
  label: string;
  /** Rendered as a link when present; the last item is never a link. */
  href?: string;
  onClick?: () => void;
}

export interface ReportHeaderProps {
  title: string;
  description?: string;
  breadcrumb?: ReadonlyArray<ReportBreadcrumbItem>;
  /** Buttons on the right of the title row (Send, Saved views, AI…). */
  actions?: ReactNode;
  /** A line under the description (missing-translations notice, date…). */
  meta?: ReactNode;
  /** ``id`` of the ``<h1>`` (the builder's modal labels itself with it). */
  titleId?: string;
}

export function ReportHeader({
  title,
  description,
  breadcrumb,
  actions,
  meta,
  titleId,
}: ReportHeaderProps): JSX.Element {
  const crumbs = breadcrumb ?? [];
  return (
    <header data-report-header="" className="min-w-0">
      {crumbs.length > 0 && (
        <nav
          aria-label="breadcrumb"
          className="mb-1 flex flex-wrap items-center gap-1.5 text-2xs text-neutral-500"
        >
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            const content =
              !last && (c.href || c.onClick) ? (
                <a
                  href={c.href ?? "#"}
                  onClick={(e) => {
                    if (c.onClick) {
                      e.preventDefault();
                      c.onClick();
                    }
                  }}
                  className="rounded-sm hover:text-neutral-800 hover:underline"
                >
                  {c.label}
                </a>
              ) : (
                <span
                  aria-current={last ? "page" : undefined}
                  className={last ? "font-medium text-neutral-700" : undefined}
                >
                  {c.label}
                </span>
              );
            return (
              <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1.5">
                {i > 0 && <ChevronRight size={12} aria-hidden="true" className="text-neutral-400" />}
                {content}
              </span>
            );
          })}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2.5 md:flex-nowrap">
        <div className="min-w-0 flex-1">
          <h1
            id={titleId}
            className="font-display text-[22px] font-extrabold leading-[1.25] tracking-[-0.5px] text-neutral-600"
          >
            {title}
          </h1>
          {description ? (
            <p
              title={description}
              className="mt-[3px] line-clamp-2 max-w-3xl text-xs leading-[1.6] text-neutral-500"
            >
              {description}
            </p>
          ) : null}
          {meta ? <div className="mt-1.5">{meta}</div> : null}
        </div>
        {actions ? (
          // max-w-full: without it the shrink-0 box keeps its max-content
          // width and, at 390 px, pushed Saved filters and AI off screen
          // instead of wrapping them (QA F02, 2026-10-26).
          <div data-report-actions="" className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}

/** The shared look of a head action: white outlined button, 11px semibold (look v3). */
export const REPORT_ACTION_CLASS =
  "inline-flex min-h-8 items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-[11px] py-[7px] text-2xs font-semibold text-neutral-700 transition-[box-shadow,transform] hover:-translate-y-px hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60";
/** The primary head action (the one the page is about): accent fill. */
export const REPORT_ACTION_PRIMARY_CLASS =
  "inline-flex min-h-8 items-center gap-1.5 rounded-xl border border-accent-dark bg-accent-dark px-[11px] py-[7px] text-2xs font-semibold text-white shadow-[0_6px_16px_rgb(var(--bi-accent)/0.15)] transition-[box-shadow,transform] hover:-translate-y-px hover:bg-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-60";
